/**
 * The words a toast or a bell row shows (twin slice 1, plan W5 step 12).
 *
 * A site audience -- FOH, `site_supervisor`, and `service_operator`, which
 * hotel staff hold (amendment A2) -- reads the robot, lift, task, edge and
 * door rules in the hotel's words: no codes, and the time on the site's
 * clock with its day named when it is not today. Ops read the
 * engine's own text, exactly as before. W5's `toastWordsFor` decides the
 * words; this file only hands it the row and the reader.
 *
 * NOT behind the attention flags (contract §8): FOH already get these
 * toasts today, carrying the engine's text, codes and all.
 */
import type { AlertViewModel } from '../../api/adapters';
import type { Audience } from '../../auth/roles';
import type { AttentionAlert } from '../attention/alert-subject';
import { toastSince, toastWordsFor, type ToastWords } from '../attention/toast-words';
import { dayClockText, type SiteTimeZone } from '../attention/words';

export type { ToastWords };

/** The words, and the time to show beside them. */
export interface AlertWords extends ToastWords {
  /**
   * The moment the hotel words' "Since" names, on the site's clock
   * ("(device time)" when the site declares no zone), its day named when
   * it is not today -- the same text as in the message: the row's opening,
   * or for a lanes-closed row the "at least" before it (`toastSince`). Set
   * only for translated words: a bell row in hotel words whose head kept
   * the device-clock time would give two different times for one moment,
   * one of them unlabelled. Null for the engine's own text, which keeps
   * the row's own time, and for a row with no opening time.
   */
  openedAt: string | null;
  /**
   * The bell row's head time, for a SITE audience: on the site's clock,
   * or labelled "(device time)" when the site declares no zone -- for
   * EVERY row, not only the rules in hotel words. A hotel reader
   * never sees an unlabelled device-clock time beside a site-clock one.
   * For hotel words it is `openedAt` (the message's own "Since"), and
   * "—" when the row has no opening time; for any other row it is the
   * row's own time (`openedAtMs`, else `timestamp`), "—" when that cannot
   * be read. Its day is named when it is not today, as the message's is.
   * Null for ops, whose head keeps the row's own time as before.
   */
  headTime: string | null;
}

/** A row's own time, epoch ms: `openedAtMs`, else the `timestamp` the
 *  legacy row shape carries (all digits = epoch ms, else a date string,
 *  the bell's long-standing rule). Null when neither can be read. */
function rowTimeMs(alert: AlertViewModel): number | null {
  const opened = alert.openedAtMs;
  if (typeof opened === 'number' && Number.isFinite(opened)) return opened;
  const ts = alert.timestamp;
  if (!ts) return null;
  const ms = /^\d+$/.test(ts) ? Number(ts) : new Date(ts).getTime();
  return Number.isFinite(ms) ? ms : null;
}

/**
 * The one place an `AlertViewModel` becomes W5's `AttentionAlert`.
 *
 * Field by field rather than a cast or a spread: if either type moves, the
 * compiler says so here, and the test beside this file says which field a
 * reader would have lost.
 */
export function attentionAlertOf(alert: AlertViewModel): AttentionAlert {
  return {
    id: alert.id,
    severity: alert.severity,
    message: alert.message,
    source: alert.source,
    rule: alert.rule,
    sourceKind: alert.sourceKind,
    sourceId: alert.sourceId,
    fingerprint: alert.fingerprint,
    openedAtMs: alert.openedAtMs,
    ackedAtMs: alert.ackedAtMs,
    ackedBySub: alert.ackedBySub,
    ackedByEmail: alert.ackedByEmail,
    ackedByRoles: alert.ackedByRoles,
    ackedByRole: alert.ackedByRole,
  };
}

/**
 * What a toast or bell row says for this alert, to this reader.
 * `wallNowMs` is this device's wall clock, read only to name a time's day.
 */
export function alertWordsFor(
  alert: AlertViewModel,
  audience: Audience,
  clock: SiteTimeZone,
  wallNowMs: number,
): AlertWords {
  const row = attentionAlertOf(alert);
  const words = toastWordsFor(row, audience, clock, wallNowMs);
  const since = words.translated ? toastSince(row) : null;
  const openedAt = since ? dayClockText(since.ms, clock, wallNowMs) : null;
  let headTime: string | null = null;
  if (audience === 'site' && words.translated) {
    // Hotel words keep exactly their own "Since" (or none): a head time
    // from another field would be a second time for the same moment.
    headTime = openedAt ?? '—';
  } else if (audience === 'site') {
    const ms = rowTimeMs(alert);
    headTime = ms === null ? '—' : dayClockText(ms, clock, wallNowMs);
  }
  return { ...words, openedAt, headTime };
}

/**
 * The row an OS notification is built from (`browser-notifications.ts`
 * reads `source` and `message`): the hotel's words when they are
 * translated, so a hidden tab never raises the code the toast and the
 * bell hide. The engine's own text passes through as the very same row.
 */
export function withAlertWords(alert: AlertViewModel, words: ToastWords): AlertViewModel {
  if (!words.translated) return alert;
  // The subtitle is the engine's text too; nothing reads it today, but it
  // goes with the message it sat under, as on the toast.
  return { ...alert, source: words.source, message: words.message, subtitle: undefined };
}
